import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import errorMessages from "../../constants/errorMessages";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { toastError, toastSuccess } from "@/utils/toastify";
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SUCCESS_MESSAGE } from "@/types";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { validateResetToken, resetPassword } = useAuth();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(true);

  // Check token validity on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("無効なリセットトークンです。");
        setTokenValid(false);
        return;
      }

      try {
        // Use AuthContext method to validate token
        const result = await validateResetToken(token);

        if (result.valid) {
          setTokenValid(true);
          setError(""); // Clear any existing errors
        } else {
          setError(result.message);
          setTokenValid(false);

          // Token expired or invalid - redirect to login after delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      } catch (error) {
        setError("トークンの検証中にエラーが発生しました。");
        setTokenValid(false);

        // Error during validation - redirect to login after delay
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    };

    validateToken();
  }, [token, validateResetToken, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password: string): boolean => {
    // Check length
    if (password.length < 8 || password.length > 32) {
      setError(errorMessages.RESET_PASSWORD_LENGTH.ja);
      return false;
    }

    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)/;
    if (!passwordRegex.test(password)) {
      setError(errorMessages.RESET_PASSWORD_FORMAT.ja);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Check if token is valid before processing
    if (tokenValid === false) {
      setError("無効なリセットトークンです。");
      setIsLoading(false);
      return;
    }

    // Validation
    if (!formData.newPassword.trim()) {
      setError(errorMessages.RESET_PASSWORD_REQUIRED.ja);
      setIsLoading(false);
      return;
    }

    if (!formData.confirmPassword.trim()) {
      setError(errorMessages.RESET_PASSWORD_CONFIRM_REQUIRED.ja);
      setIsLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(errorMessages.RESET_PASSWORD_NOT_MATCH.ja);
      setIsLoading(false);
      return;
    }

    if (!validatePassword(formData.newPassword)) {
      setIsLoading(false);
      return;
    }

    try {
      // Use AuthContext method with correct data format for backend
      const result = await resetPassword(token!, {
        password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });

      if (result.success) {
        toastSuccess(result.message || DEFAULT_SUCCESS_MESSAGE);
        navigate("/login");
      } else {
        setError(result.message);
        toastError(result?.message || DEFAULT_ERROR_MESSAGE);
      }
    } catch (err: any) {
      toastError(err?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      setError("エラーが発生しました。もう一度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  const disableAction = isLoading || !tokenValid;

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-0">
      <div className="w-full max-w-[600px] mx-auto px-8 py-12">
        <div className="w-full">
          <h2 className="text-[32px] font-bold text-[#333] text-center mb-10">
            パスワードを再設定
          </h2>

          {/* Show form if token is valid */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="mb-6 relative">
              <Input
                type={"password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
                disabled={disableAction}
                label="新しいパスワード"
                className="w-full pr-12"
              />
            </div>

            <div className="mb-6 relative">
              <Input
                type={"password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
                disabled={disableAction}
                label="新しいパスワード（確認）"
                className="w-full pr-12"
              />
            </div>

            <Button
              type="submit"
              buttonType="primary"
              className="text-[15px] px-4 py-2.5 h-[45px] w-[17%] mb-6 mx-auto relative overflow-hidden"
              disabled={disableAction}
              isLoading={isLoading}
            >
              <span className="flex items-center justify-center h-full relative">
                確定
              </span>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
